import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Package, Truck, Shield, Clock, Check, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useCart } from '../../context/CartContext';
import { swagAPI } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import RecentlyViewed from '../../components/RecentlyViewed';

const SwagProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { recentlyViewed, addToRecentlyViewed } = useRecentlyViewed();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  
  const [selectedOptions, setSelectedOptions] = useState({
    color: '',
    quantity: 0
  });

  // Fetch product from API
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await swagAPI.getProduct(productId);
        // Handle both { product: {...} } and direct product response
        const data = response.product || response;
        setProduct(data);
        // Set initial options
        setSelectedOptions({
          color: data.colors?.[0] || '',
          quantity: data.minQuantity || data.min_quantity || 6
        });
        // Add to recently viewed
        addToRecentlyViewed({
          ...data,
          section: 'swag'
        });
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, addToRecentlyViewed]);

  // Fetch pricing tiers from API
  useEffect(() => {
    const fetchPricingTiers = async () => {
      if (!product?.sku && !product?.id) return;
      
      try {
        setTiersLoading(true);
        const productSku = product.sku || product.id;
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pricing/tiers/${productSku}`);
        const data = await response.json();
        
        if (data.tiers && data.tiers.length > 0) {
          setPricingTiers(data.tiers);
        }
      } catch (err) {
        console.error('Error fetching pricing tiers:', err);
        // Fallback to default tiers if API fails
      } finally {
        setTiersLoading(false);
      }
    };
    
    if (product) {
      fetchPricingTiers();
    }
  }, [product]);

  // Calculate tier prices - use API tiers if available, otherwise use defaults
  const tierPrices = useMemo(() => {
    // If we have API tiers, use them
    if (pricingTiers.length > 0) {
      return pricingTiers.map(tier => ({
        min: tier.min_qty,
        max: tier.max_qty,
        label: tier.label,
        unitPrice: tier.unit_price,
        markup: tier.markup_percent,
        flatAddition: tier.flat_addition || 0
      }));
    }
    
    // Fallback to default calculation if no API tiers
    if (!product) return [];
    const costPrice = product.cost_price || (product.retail_price / 4) || 0;
    
    // Default tier breakpoints
    const defaultTiers = [
      { min: 1, max: 2, label: '1-2', markup: 350 },
      { min: 3, max: 24, label: '3-24', markup: 300 },
      { min: 25, max: 49, label: '25-49', markup: 285 },
      { min: 50, max: 99, label: '50-99', markup: 270 },
      { min: 100, max: null, label: '100+', markup: 250 },
    ];
    
    return defaultTiers.map(tier => ({
      ...tier,
      unitPrice: costPrice * (1 + tier.markup / 100),
      flatAddition: 0
    }));
  }, [product, pricingTiers]);

  const { calculatedPrice, totalPrice, quantityOptions } = useMemo(() => {
    if (!product) return { calculatedPrice: 0, totalPrice: 0, quantityOptions: [] };
    
    // Find applicable tier based on quantity
    const qty = selectedOptions.quantity;
    const applicableTier = tierPrices.find(t => qty >= t.min && (t.max === null || qty <= t.max));
    const unitPrice = applicableTier?.unitPrice || product.retail_price || product.basePrice || 0;
    
    // Build quantity options based on tier breakpoints
    const baseOptions = [1, 6, 12, 24, 25, 50, 75, 100, 250, 500];
    const tierMinQtys = tierPrices.map(t => t.min);
    const allOptions = [...new Set([...baseOptions, ...tierMinQtys])].sort((a, b) => a - b);
    
    return {
      calculatedPrice: unitPrice,
      totalPrice: unitPrice * qty,
      quantityOptions: allOptions.filter(q => q <= 500)
    };
  }, [product, selectedOptions.quantity, tierPrices]);

  const handleOptionChange = (optionType, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionType]: optionType === 'quantity' ? parseInt(value) : value
    }));
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    const cartItem = {
      ...product,
      selectedOptions: {
        ...selectedOptions,
        size: 'Standard',
        finish: selectedOptions.color,
        stock: 'Standard',
        turnaround: '5-7 Business Days'
      }
    };
    
    addToCart(cartItem, cartItem.selectedOptions, 1);
    toast({
      title: "Added to cart!",
      description: `${product.name} (${selectedOptions.quantity} pcs) has been added to your cart.`,
      duration: 3000,
    });
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="p-12 rounded-2xl bg-ivory-100 border border-ivory-300/50 shadow-luxury text-center">
          <p className="text-ivory-600 text-lg mb-4">Product not found</p>
          <Button asChild className="btn-luxury-primary">
            <Link to="/Swag/products">Browse Products</Link>
          </Button>
        </div>
      </div>
    );
  }
  
  const features = [
    { icon: Package, text: 'Premium quality materials' },
    { icon: Clock, text: '5-7 day turnaround' },
    { icon: Shield, text: 'Satisfaction guaranteed' },
    { icon: Truck, text: 'Fast & reliable shipping' },
  ];

  return (
    <div className="min-h-screen bg-ivory-50">
      <div className="container mx-auto px-4 py-8">
        <Link 
          to="/Swag/products" 
          className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-8 transition-colors font-body"
          data-testid="back-to-swag-products"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Promotional Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="aspect-square rounded-2xl overflow-hidden bg-ivory-100 border border-ivory-300/50 shadow-luxury">
              <img
                src={product.image_url || product.image || 'https://placehold.co/600x600/f5f3f0/9a8b7d?text=Product'}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://placehold.co/600x600/f5f3f0/9a8b7d?text=Product';
                }}
              />
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <p className="text-gold-600 font-mono text-sm mb-2">{product.sku}</p>
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-ivory-800 mb-4" data-testid="swag-product-name">
                {product.name}
              </h1>
              <p className="text-lg text-ivory-500 leading-relaxed font-body">
                {product.description}
              </p>
            </div>

            {/* Pricing */}
            <div className="p-6 rounded-2xl gradient-champagne border border-gold-500/20">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold text-gold-600" data-testid="swag-product-price">
                  ${calculatedPrice.toFixed(2)}
                </span>
                <span className="text-ivory-500 font-body">per unit</span>
              </div>
              <p className="text-sm text-ivory-500 mb-4 font-body">
                Total for {selectedOptions.quantity} units: <span className="text-ivory-800 font-bold">${totalPrice.toFixed(2)}</span>
              </p>
              
              {/* Volume Pricing Tiers Table */}
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-ivory-700 mb-3 font-body">Volume Pricing</h4>
                {tiersLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 text-gold-500 animate-spin" />
                  </div>
                ) : tierPrices.length > 0 ? (
                  <>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
                      {tierPrices.map((tier, index) => {
                        const isActive = selectedOptions.quantity >= tier.min && 
                          (tier.max === null || selectedOptions.quantity <= tier.max);
                        return (
                          <div 
                            key={index}
                            className={`p-3 rounded-lg font-body transition-all cursor-pointer ${
                              isActive 
                                ? 'bg-gold-500 text-white shadow-lg scale-105' 
                                : 'bg-ivory-200/70 hover:bg-ivory-300/70'
                            }`}
                            onClick={() => handleOptionChange('quantity', tier.min)}
                            data-testid={`tier-${tier.label}`}
                          >
                            <div className={`text-xs font-medium ${isActive ? 'text-gold-100' : 'text-ivory-500'}`}>
                              {tier.label}
                            </div>
                            <div className={`text-sm font-bold ${isActive ? 'text-white' : 'text-ivory-800'}`}>
                              ${tier.unitPrice.toFixed(2)}
                            </div>
                            {tier.flatAddition > 0 && (
                              <div className={`text-xs ${isActive ? 'text-gold-200' : 'text-amber-600'}`}>
                                +${tier.flatAddition.toFixed(2)} setup
                              </div>
                            )}
                            {index > 0 && tierPrices[0]?.unitPrice > 0 && (
                              <div className={`text-xs ${isActive ? 'text-gold-200' : 'text-green-600'}`}>
                                Save {Math.round((1 - tier.unitPrice / tierPrices[0].unitPrice) * 100)}%
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-ivory-500 mt-3 font-body text-center">
                      Click a tier to select that quantity • Higher quantities = bigger savings!
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-ivory-500 text-center py-2">
                    Contact us for volume pricing on this item.
                  </p>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="p-6 rounded-2xl bg-ivory-100 border border-ivory-300/50 shadow-luxury">
              <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-6">Configure Your Order</h3>
              <div className="space-y-5">
                {/* Color Selection */}
                {product.colors && product.colors.length > 0 && (
                  <div>
                    <Label htmlFor="color" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Color</Label>
                    <Select value={selectedOptions.color} onValueChange={(value) => handleOptionChange('color', value)}>
                      <SelectTrigger id="color" className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="swag-color-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-ivory-50 border-ivory-300">
                        {product.colors.map((color) => (
                          <SelectItem key={color} value={color} className="text-ivory-700 hover:bg-gold-500/10 font-body">{color}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Quantity Selection */}
                <div>
                  <Label htmlFor="quantity" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Quantity</Label>
                  <Select 
                    value={selectedOptions.quantity.toString()} 
                    onValueChange={(value) => handleOptionChange('quantity', value)}
                  >
                    <SelectTrigger id="quantity" className="bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="swag-quantity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ivory-50 border-ivory-300">
                      {quantityOptions.map((qty) => (
                        <SelectItem key={qty} value={qty.toString()} className="text-ivory-700 hover:bg-gold-500/10 font-body">
                          {qty} units - ${(product.pricing?.[qty] || calculatedPrice).toFixed(2)} each
                        </SelectItem>
                      ))}
                      <SelectItem value="500" className="text-ivory-700 hover:bg-gold-500/10 font-body">
                        500 units - Contact for pricing
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-ivory-500 mt-2 font-body">Minimum order: {product.minQuantity || product.min_quantity || 1} units</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full text-lg py-6 btn-luxury-primary rounded-xl shadow-luxury"
                onClick={handleBuyNow}
                data-testid="swag-buy-now-btn"
              >
                Buy Now - ${totalPrice.toFixed(2)}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full text-lg py-6 btn-luxury-outline rounded-xl"
                onClick={handleAddToCart}
                data-testid="swag-add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 rounded-xl bg-ivory-100 border border-ivory-300/50"
                >
                  <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-gold-600" />
                  </div>
                  <span className="text-sm text-ivory-600 font-body">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Customization Note */}
            <div className="p-4 rounded-xl gradient-champagne border border-gold-500/20">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-gold-600 mt-0.5" />
                <div>
                  <p className="text-ivory-700 font-medium font-body">Custom Branding Available</p>
                  <p className="text-sm text-ivory-500 font-body">Contact us for logo placement and imprint options.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recently Viewed Section */}
        <RecentlyViewed products={recentlyViewed} currentProductId={productId} />
      </div>
    </div>
  );
};

export default SwagProductDetail;
